import {
  useAddRegionPageModule,
  useGetRegionPageModules,
  useUpdateRegionPageModule,
} from 'api/regions/hooks';
import { REGION, REGION_PAGE_MODULES } from 'constants/routes';
import { moduleNames, modules as storeModules } from 'pages/Stores/components/modules';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { toastr } from 'utils';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { useRegionArtistAvailableItems } from 'pages/ModulePage/moduleTypes/artists/useArtistAvailableItems';
import { useRegionBannerAvailableItems } from 'pages/ModulePage/moduleTypes/banner/useBannerAvailableItems';
import { useRegionProductAvailableItems } from 'pages/ModulePage/moduleTypes/favoriteProducts/useProductAvailableItems';
import { useRegionLocationAvailableItems } from 'pages/ModulePage/moduleTypes/locations/useLocationAvailableItems';
import { useRegionLookAvailableItems } from 'pages/ModulePage/moduleTypes/looks/useLookAvailableItems';
import { useRegionVideoAvailableItems } from 'pages/ModulePage/moduleTypes/video/useVideoAvailableItems';
import { useRegionEventAvailableItems } from 'pages/ModulePage/moduleTypes/events/useEventAvailableItems';
import PageModulePage from './ModulePage';
import { pageModuleTypeKeys, pageModuleTypes } from './pageModuleTypes';

const regionAvailableItemsHooks = {
  [moduleNames.MULTIPLE_LOOK]: useRegionLookAvailableItems,
  [moduleNames.FAVORITE_PRODUCTS]: useRegionProductAvailableItems,
  [moduleNames.BANNER]: useRegionBannerAvailableItems,
  [moduleNames.VIDEO]: useRegionVideoAvailableItems,
  [moduleNames.ARTISTS]: useRegionArtistAvailableItems,
  [moduleNames.LOCATIONS]: useRegionLocationAvailableItems,
  [moduleNames.EVENTS]: useRegionEventAvailableItems,
};

const RegionModulePage = () => {
  const { moduleUid } = useParams();
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'pageModules' });
  const { t: tApp } = useTranslation('app');
  const navigate = useNavigate();
  const selectedRegion = useSelectedProfileStore((state) => state.selectedRegionUid);
  const { data: pageModules = [], isLoading, isError } = useGetRegionPageModules(selectedRegion);
  const { mutate: addRegionPageModule, isPending } = useAddRegionPageModule();
  const { mutate: updateRegionPageModule, isPending: isUpdatePending } =
    useUpdateRegionPageModule();
  const isEditMode = !!moduleUid;

  const moduleTypes = useMemo(() => {
    const enabledModuleNames = [
      ...storeModules.filter((module) => isEditMode || !module.disabled).map((m) => m.name),
      moduleNames.LOCATIONS,
    ];

    return Object.fromEntries(
      enabledModuleNames
        .filter((name) => pageModuleTypes[name])
        .map((name) => [name, pageModuleTypes[name](regionAvailableItemsHooks[name])]),
    );
  }, [isEditMode]);

  const existingModule = useMemo(
    () => pageModules.find((module) => module.uid === moduleUid),
    [moduleUid, pageModules],
  );
  const initialModuleTypeKey = existingModule
    ? pageModuleTypeKeys[existingModule.module_type]
    : null;

  useEffect(() => {
    if (isEditMode && selectedRegion && !isLoading && !isError && !existingModule) {
      navigate(REGION_PAGE_MODULES, { replace: true });
    }
  }, [existingModule, isEditMode, isError, isLoading, navigate, selectedRegion]);

  const handleSave = async (moduleTypeKey, values, { skipContentUpdate = false } = {}) => {
    const moduleMeta = moduleTypes[moduleTypeKey];
    if (!moduleMeta || !selectedRegion) return;

    try {
      const content = skipContentUpdate
        ? undefined
        : await moduleMeta.buildPayload(values, { isEditMode });

      if (isEditMode) {
        updateRegionPageModule(
          {
            regionUid: selectedRegion,
            moduleUid,
            moduleType: existingModule?.module_type || moduleMeta.moduleName.toLowerCase(),
            name: values.pageModuleName,
            content,
          },
          {
            onSuccess: () => navigate(REGION_PAGE_MODULES),
          },
        );
        return;
      }

      addRegionPageModule(
        {
          regionUid: selectedRegion,
          moduleType: moduleMeta.moduleName.toLowerCase(),
          name: values.pageModuleName,
          order: pageModules.length || 0,
          content,
        },
        {
          onSuccess: (data) =>
            navigate(REGION_PAGE_MODULES, { state: { scrollToItemId: data.uid } }),
        },
      );
    } catch {
      toastr.error(t('failedToCreatePageModule'));
    }
  };

  if (isEditMode && !existingModule) {
    if (!isLoading && !isError) return null;
  }

  const currentBreadcrumbLabel = isEditMode ? (
    isLoading ? (
      <Skeleton width={120} />
    ) : (
      existingModule?.name || t('editModule')
    )
  ) : (
    t('createModule')
  );

  return (
    <PageModulePage
      moduleTypes={moduleTypes}
      onSave={handleSave}
      initialModuleTypeKey={initialModuleTypeKey}
      existingModule={existingModule}
      isPending={isPending || isUpdatePending}
      title={t(isEditMode ? 'editModule' : 'createModule')}
      description={t('moduleDetailsSubtitle')}
      saveLabel={t(isEditMode ? 'editModule' : 'createModule')}
      breadcrumbs={[
        { label: tApp('breadcrumbs.region'), to: REGION },
        { label: tApp('breadcrumbs.pageModules'), to: REGION_PAGE_MODULES },
        { label: currentBreadcrumbLabel },
      ]}
      loading={isLoading}
      isEditMode={isEditMode}
    />
  );
};

export default RegionModulePage;
